import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Calendar,
  Clock,
  Download,
  FileText,
  Filter,
  Search,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiService from "../api/apiService";
import { getInvoiceByBookingId } from "../utils/getInvoiceByBookingId";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import "./Dashboard.module.css";
import styles from "./Deal.module.css";
import EditBookingModal from "./EditBookingModal";
import { Gheader } from "./Gheader";
import InvoiceModal from "./InvoiceModal";
import { Sidebar } from "./Sidebar";

// FIX: Import jsPDF and jspdf-autotable directly to use them within the component.
import { Spin } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useData } from "../context/DataContext";

const Booking = () => {
  const { data, loading } = useData();
  const { guests, invoices } = data || {};
  const hotelId = localStorage.getItem("hotel");
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    id: null,
    roomId: null,
  });
  const [allInvoices, setAllInvoices] = useState([]);
  const [currentInvoice, setCurrentInvoice] = useState();
  const [dataChanged, setDataChanged] = useState(0);

  // Date filter states
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
    enabled: false,
  });

  // Revenue filter states
  const [revenueFilter, setRevenueFilter] = useState({
    startDate: "",
    endDate: "",
    enabled: false,
  });

  const rowsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [dataChanged]);

  // Create a memoized fetchData function that we can reuse
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      // Fetch all data in parallel for better performance
      const [bookingsResponse] = await Promise.all([
        apiService.get(`/bookings/?hotel=${hotelId}&is_archive=false`),
      ]);

      // First filter non-archived bookings
      const activeBookings = bookingsResponse.filter(
        (booking) => !booking.is_archive,
      );

      // Then extract IDs of active bookings
      const activeBookingIds = new Set(activeBookings.map((b) => b.id));

      // Filter invoices whose booking is in the active bookings list
      const filteredInvoices = invoices.filter((invoice) =>
        activeBookingIds.has(invoice.booking),
      );

      // Now set the state
      setBookings(activeBookings);
      setAllInvoices(filteredInvoices);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching data:", error);
      toast.error("Failed to load some data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshData = () => {
    // Increment dataChanged to trigger a re-fetch
    setDataChanged((prev) => prev + 1);
  };

  const deleteBooking = async () => {
    try {
      const roomId = deleteConfirm.roomId;
      const response = await axios.patch(
        `https://hotelproapi.defconinnovations.com/api/bookings/${deleteConfirm.id}/?hotel=${hotelId}`,
        {
          is_archive: true,
        },
      );

      if (response.status === 200 || response.status === 204) {
        try {
          const updatedRoomStatus = {
            status: "available",
          };
          await apiService.patch(
            `/rooms/${roomId}/?hotel=${hotelId}`,
            updatedRoomStatus,
          );
          toast.success("Booking deleted successfully!");
          toast.success("Room status updated to Available!");
        } catch (error) {
          console.error("Failed to update room status:", error);
        }

        // Update local state immediately for instant UI response
        setBookings((prevBookings) =>
          prevBookings.filter((booking) => booking.id !== deleteConfirm.id),
        );

        // Refresh data to ensure everything is in sync
        refreshData();
      }
    } catch (error) {
      toast.error("Failed to delete! Please try again.");
      console.log(error);
    } finally {
      setDeleteConfirm({ isOpen: false, id: null, roomId: null });
    }
  };

  // Helper function to check if a date is within range
  const isDateInRange = (dateStr, startDate, endDate) => {
    if (!startDate && !endDate) return true;
    const date = new Date(dateStr);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start && end) {
      return date >= start && date <= end;
    } else if (start) {
      return date >= start;
    } else if (end) {
      return date <= end;
    }
    return true;
  };

  // Filter bookings based on date range
  const getDateFilteredBookings = () => {
    if (!dateFilter.enabled) {
      return bookings;
    }

    return bookings.filter((booking) => {
      const checkInDate = booking.check_in;
      const checkOutDate = booking.check_out;

      // Check if either check-in or check-out date falls within the range
      return (
        isDateInRange(checkInDate, dateFilter.startDate, dateFilter.endDate) ||
        isDateInRange(checkOutDate, dateFilter.startDate, dateFilter.endDate)
      );
    });
  };

  // Enhanced search function
  const searchBookings = (bookings, searchTerm) => {
    if (!searchTerm.trim()) return bookings;

    const term = searchTerm.toLowerCase().trim();

    return bookings.filter((booking) => {
      // Search in guest name
      const guestName = getGuestName(booking.guest).toLowerCase();

      // Search in booking ID
      const bookingId = booking.id ? booking.id.toString().toLowerCase() : "";

      // Search in room number
      const roomNumber =
        booking.room && Array.isArray(booking.room)
          ? booking.room
              .map((r) => r.room_number)
              .join(" ")
              .toLowerCase()
          : booking.room?.room_number?.toLowerCase() || "";

      // Search in room type
      const roomType =
        booking.room && Array.isArray(booking.room)
          ? booking.room
              .map((r) => r.room_type_name)
              .join(" ")
              .toLowerCase()
          : booking.room?.room_type_name?.toLowerCase() || "";

      // Search in source
      const source = booking.source ? booking.source.toLowerCase() : "";

      // Search in status
      const status = booking.status ? booking.status.toLowerCase() : "";

      return (
        guestName.includes(term) ||
        bookingId.includes(term) ||
        roomNumber.includes(term) ||
        roomType.includes(term) ||
        source.includes(term) ||
        status.includes(term)
      );
    });
  };

  const filteredBookings = (() => {
    let filtered = getDateFilteredBookings();

    // Filter by active tab
    if (activeTab !== "all") {
      filtered = filtered.filter((booking) => booking.source === activeTab);
    }

    // Apply search
    filtered = searchBookings(filtered, searchTerm);

    return filtered;
  })();

  const totalPages = Math.max(
    1,
    Math.ceil(filteredBookings.length / rowsPerPage),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredBookings.slice(indexOfFirstRow, indexOfLastRow);

  // Get revenue stats with date filtering
  const getRevenueStats = () => {
    let revenueBookings = bookings;

    if (revenueFilter.enabled) {
      revenueBookings = bookings.filter((booking) => {
        const checkInDate = booking.check_in;
        return isDateInRange(
          checkInDate,
          revenueFilter.startDate,
          revenueFilter.endDate,
        );
      });
    }

    const revenue =
      allInvoices && allInvoices.length > 0
        ? allInvoices
            .filter((invoice) => {
              if (!revenueFilter.enabled) return true;

              const booking = bookings.find((b) => b.id === invoice.booking);
              /* console.log(booking); */
              if (!booking) {
                console.warn(
                  "No matching booking for invoice ID:",
                  invoice.id,
                  "Booking ID:",
                  invoice.booking,
                );
                return false;
              }

              const inRange = isDateInRange(
                booking.check_in,
                revenueFilter.startDate,
                revenueFilter.endDate,
              );

              /* console.log('Filtering Invoice:', {
                invoiceId: invoice.id,
                bookingId: invoice.booking,
                check_in: booking.check_in,
                startDate: revenueFilter.startDate,
                endDate: revenueFilter.endDate,
                inRange: inRange,
              }); */

              return inRange;
            })
            .reduce((sum, invoice) => {
              const amount = parseFloat(invoice.total_amount) || 0;
              /* console.log('Adding invoice amount:', {
                invoiceId: invoice.id,
                amount: amount,
              }); */
              return sum + amount;
            }, 0)
        : revenueBookings.reduce((sum, booking) => {
            const amount = parseFloat(booking.amount) || 0;
            /* console.log('Fallback booking revenue:', {
              bookingId: booking.id,
              amount: amount,
            }); */
            return sum + amount;
          }, 0);

    /*     console.log('Final Calculated Revenue:', revenue);
     */
    return {
      revenue,
      bookingCount: revenueBookings.length,
      dateRange: revenueFilter.enabled
        ? `${revenueFilter.startDate || "Start"} to ${
            revenueFilter.endDate || "End"
          }`
        : "All Time",
    };
  };

  const getStats = () => {
    const dateFilteredBookings = getDateFilteredBookings();
    const revenueStats = getRevenueStats();

    return {
      totalBookings: dateFilteredBookings.length,
      checkedIn: dateFilteredBookings.filter((b) => b.status === "check_in")
        .length,
      upcoming: dateFilteredBookings.filter((b) => b.status === "confirmed")
        .length,
      revenue: revenueStats.revenue,
      revenueBookingCount: revenueStats.bookingCount,
      revenueDateRange: revenueStats.dateRange,
    };
  };

  const stats = getStats();

  const getStatusColor = (status) => {
    const colors = {
      confirmed: "bg-success text-white",
      pending: "bg-warning text-white",
      cancelled: "bg-danger text-white",
      check_in: "bg-primary text-white",
      check_out: "bg-secondary text-white",
    };
    return colors[status] || "bg-light text-dark";
  };

  const getGuestName = (currentGuestId) => {
    const matchingGuest = guests?.find((guest) => guest.id === currentGuestId);
    return matchingGuest ? matchingGuest.name : "Guest not found";
  };

  const confirmDelete = (id, roomId) => {
    if (!id) return;
    setDeleteConfirm({ isOpen: true, id, roomId });
  };

  const formatStatus = (status) => {
    const statusMap = {
      check_in: "Checked-in",
      check_out: "Checked-out",
    };

    return (
      statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  // Helper function to safely format currency amounts
  const formatCurrency = (amount) => {
    // Check if the amount is valid and can be converted to a number
    if (amount === null || amount === undefined || isNaN(Number(amount))) {
      return "₹0.00"; // Return a default value for invalid amounts
    }

    // Format the valid amount
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  // Helper function to safely get booking amount
  const getBookingAmount = (booking) => {
    // First try to get amount from invoice
    const invoice = getInvoiceByBookingId(booking.id, allInvoices);
    const invoiceAmount = invoice?.total_amount;

    // If invoice amount exists and is valid, use it
    if (
      invoiceAmount !== null &&
      invoiceAmount !== undefined &&
      !isNaN(Number(invoiceAmount))
    ) {
      return invoiceAmount;
    }

    return booking.amount || 0;
  };

  function filterInvoiceFile(allInvoices, bookingId) {
    const matchingInvoice = allInvoices.find(
      (invoice) => invoice.booking === bookingId,
    );

    // If a matching invoice is found, return the invoice_file value
    if (matchingInvoice) {
      return matchingInvoice.ivoice_file;
    }

    // If no matching invoice is found, return null or handle as needed
    return null;
  }

  // PDF Generation Functions (Fixed)
  const generateBookingsPDF = () => {
    try {
      // FIX: Instantiate jsPDF directly from the imported module.
      const doc = new jsPDF();

      const dateRange = dateFilter.enabled
        ? `${dateFilter.startDate || "Start"} to ${dateFilter.endDate || "End"}`
        : "All Time";

      // Title
      doc.setFontSize(20);
      doc.text("Booking Report", 20, 20);

      // Date range
      doc.setFontSize(12);
      doc.text(`Date Range: ${dateRange}`, 20, 35);
      doc.text(`Total Bookings: ${filteredBookings.length}`, 20, 45);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 55);

      // Table data
      const tableData = filteredBookings.map((booking) => [
        booking.id,
        getGuestName(booking.guest),
        `${booking.check_in} - ${booking.check_out}`,
        booking.room && Array.isArray(booking.room)
          ? booking.room.map((r) => r.room_type_name).join(", ")
          : booking.room?.room_type_name || "",
        booking.room && Array.isArray(booking.room)
          ? booking.room.map((r) => r.room_number).join(", ")
          : booking.room?.room_number || "",
        booking.source === "walk_in"
          ? "Walk-in"
          : booking.source.charAt(0).toUpperCase() + booking.source.slice(1),
        formatStatus(booking.status),
        formatCurrency(getBookingAmount(booking)),
      ]);

      // Table headers
      const headers = [
        "Booking ID",
        "Guest",
        "Check In/Out",
        "Room Type",
        "Room No.",
        "Source",
        "Status",
        "Amount",
      ];

      // Use autoTable if available (it should be, due to the import)
      if (doc.autoTable) {
        doc.autoTable({
          head: [headers],
          body: tableData,
          startY: 65,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [66, 139, 202] },
        });
      } else {
        // Fallback in case autoTable fails to load
        console.warn("jspdf-autotable not loaded. Using basic table.");
        let yPosition = 70;
        doc.setFontSize(10);
        doc.text(headers.join(" | "), 20, yPosition);
        yPosition += 10;
        tableData.forEach((row) => {
          doc.text(row.join(" | "), 20, yPosition);
          yPosition += 8;
        });
      }

      doc.save(`booking-report-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF report generated successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const generateRevenuePDF = () => {
    try {
      // FIX: Instantiate jsPDF directly from the imported module.
      const doc = new jsPDF();
      const revenueStats = getRevenueStats();

      // Title
      doc.setFontSize(20);
      doc.text("Revenue Report", 20, 20);

      // Revenue summary
      doc.setFontSize(12);
      doc.text(`Date Range: ${revenueStats.dateRange}`, 20, 35);
      doc.text(
        `Total Revenue: ${formatCurrency(revenueStats.revenue)}`,
        20,
        45,
      );
      doc.text(`Total Bookings: ${revenueStats.bookingCount}`, 20, 55);
      doc.text(
        `Average Revenue per Booking: ${formatCurrency(
          revenueStats.revenue / revenueStats.bookingCount || 0,
        )}`,
        20,
        65,
      );
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 75);

      // Revenue breakdown by source
      const sourceRevenue = {};
      const relevantBookings = revenueFilter.enabled
        ? bookings.filter((booking) =>
            isDateInRange(
              booking.check_in,
              revenueFilter.startDate,
              revenueFilter.endDate,
            ),
          )
        : bookings;

      relevantBookings.forEach((booking) => {
        const source =
          booking.source === "walk_in"
            ? "Walk-in"
            : booking.source.charAt(0).toUpperCase() + booking.source.slice(1);
        const amount = getBookingAmount(booking);
        sourceRevenue[source] =
          (sourceRevenue[source] || 0) + parseFloat(amount || 0);
      });

      // Revenue breakdown table
      const revenueTableData = Object.entries(sourceRevenue).map(
        ([source, revenue]) => [
          source,
          formatCurrency(revenue),
          `${((revenue / revenueStats.revenue) * 100).toFixed(1)}%`,
        ],
      );

      // Use autoTable if available
      if (doc.autoTable) {
        doc.autoTable({
          head: [["Source", "Revenue", "Percentage"]],
          body: revenueTableData,
          startY: 85,
          styles: { fontSize: 10 },
          headStyles: { fillColor: [40, 167, 69] },
        });
      } else {
        // Fallback in case autoTable fails to load
        console.warn("jspdf-autotable not loaded. Using basic table.");
        let yPosition = 90;
        doc.setFontSize(10);
        doc.text("Source | Revenue | Percentage", 20, yPosition);
        yPosition += 10;
        revenueTableData.forEach((row) => {
          doc.text(row.join(" | "), 20, yPosition);
          yPosition += 8;
        });
      }

      doc.save(`revenue-report-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("Revenue PDF report generated successfully!");
    } catch (error) {
      console.error("Error generating revenue PDF:", error);
      toast.error("Failed to generate revenue PDF. Please try again.");
    }
  };

  // Clear date filters
  const clearDateFilter = () => {
    setDateFilter({ startDate: "", endDate: "", enabled: false });
  };

  const clearRevenueFilter = () => {
    setRevenueFilter({ startDate: "", endDate: "", enabled: false });
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className={styles.gheader}>
      <div className={styles.dashboardContainer}>
        <Sidebar />
        <div className={styles.mainContent}>
          <div className="min-vh-100 bg-light">
            <Gheader />

            {/* Improved Date Filter Section */}
            <Container fluid className="mb-4">
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-gradient-primary text-white border-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5
                      className="mb-0 d-flex align-items-center"
                      style={{ color: "black" }}
                    >
                      <Filter className="me-2" size={20} />
                      Filters & Reports
                    </h5>
                    <div className="d-flex gap-2">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={generateBookingsPDF}
                        disabled={filteredBookings.length === 0}
                        className="d-flex align-items-center"
                        style={{ color: "#0d6efd" }}
                      >
                        <Download className="me-1" size={16} />
                        Bookings PDF
                      </Button>
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={generateRevenuePDF}
                        className="d-flex align-items-center"
                        style={{ color: "green" }}
                      >
                        <FileText className="me-1" size={16} />
                        Revenue PDF
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col lg={6}>
                      <div
                        className="border rounded-3 p-3 h-100"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 text-primary">
                            <Calendar className="me-2" size={18} />
                            Booking Date Filter
                          </h6>
                          {dateFilter.enabled && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={clearDateFilter}
                              className="d-flex align-items-center"
                            >
                              <X size={14} className="me-1" />
                              Clear
                            </Button>
                          )}
                        </div>

                        <Form.Check
                          type="switch"
                          id="booking-date-switch"
                          label="Enable booking date filter"
                          checked={dateFilter.enabled}
                          onChange={(e) =>
                            setDateFilter({
                              ...dateFilter,
                              enabled: e.target.checked,
                            })
                          }
                          className="mb-3"
                        />

                        <Row className="g-3">
                          <Col>
                            <Form.Group>
                              <Form.Label className="small fw-medium">
                                From Date
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={dateFilter.startDate}
                                onChange={(e) =>
                                  setDateFilter({
                                    ...dateFilter,
                                    startDate: e.target.value,
                                  })
                                }
                                disabled={!dateFilter.enabled}
                                className="form-control-sm"
                              />
                            </Form.Group>
                          </Col>
                          <Col>
                            <Form.Group>
                              <Form.Label className="small fw-medium">
                                To Date
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={dateFilter.endDate}
                                onChange={(e) =>
                                  setDateFilter({
                                    ...dateFilter,
                                    endDate: e.target.value,
                                  })
                                }
                                disabled={!dateFilter.enabled}
                                className="form-control-sm"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        {dateFilter.enabled &&
                          (dateFilter.startDate || dateFilter.endDate) && (
                            <div className="mt-3 p-2 bg-primary bg-opacity-10 rounded-2">
                              <small className="text-primary fw-medium">
                                Active Filter: {dateFilter.startDate || "Start"}{" "}
                                → {dateFilter.endDate || "End"}
                              </small>
                            </div>
                          )}
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div
                        className="border rounded-3 p-3 h-100"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 text-success">
                            <Badge bg="success" className="me-2">
                              ₹
                            </Badge>
                            Revenue Date Filter
                          </h6>
                          {revenueFilter.enabled && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={clearRevenueFilter}
                              className="d-flex align-items-center"
                            >
                              <X size={14} className="me-1" />
                              Clear
                            </Button>
                          )}
                        </div>

                        <Form.Check
                          type="switch"
                          id="revenue-date-switch"
                          label="Enable revenue date filter"
                          checked={revenueFilter.enabled}
                          onChange={(e) =>
                            setRevenueFilter({
                              ...revenueFilter,
                              enabled: e.target.checked,
                            })
                          }
                          className="mb-3"
                        />

                        <Row className="g-3">
                          <Col>
                            <Form.Group>
                              <Form.Label className="small fw-medium">
                                From Date
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={revenueFilter.startDate}
                                onChange={(e) =>
                                  setRevenueFilter({
                                    ...revenueFilter,
                                    startDate: e.target.value,
                                  })
                                }
                                disabled={!revenueFilter.enabled}
                                className="form-control-sm"
                              />
                            </Form.Group>
                          </Col>
                          <Col>
                            <Form.Group>
                              <Form.Label className="small fw-medium">
                                To Date
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={revenueFilter.endDate}
                                onChange={(e) =>
                                  setRevenueFilter({
                                    ...revenueFilter,
                                    endDate: e.target.value,
                                  })
                                }
                                disabled={!revenueFilter.enabled}
                                className="form-control-sm"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        {revenueFilter.enabled &&
                          (revenueFilter.startDate ||
                            revenueFilter.endDate) && (
                            <div className="mt-3 p-2 bg-success bg-opacity-10 rounded-2">
                              <small className="text-success fw-medium">
                                Active Filter:{" "}
                                {revenueFilter.startDate || "Start"} →{" "}
                                {revenueFilter.endDate || "End"}
                              </small>
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Container>

            {/* Stats Grid */}
            <Row className="g-4 mb-5 mt-1 px-3">
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Total Bookings</p>
                      <h3 className="fs-2 fw-bold">{stats.totalBookings}</h3>
                      {dateFilter.enabled && (
                        <small className="text-info">
                          Filtered: {dateFilter.startDate || "Start"} to{" "}
                          {dateFilter.endDate || "End"}
                        </small>
                      )}
                    </div>
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Checked In</p>
                      <h3 className="fs-2 fw-bold">{stats.checkedIn}</h3>
                    </div>
                    <Users className="w-6 h-6 text-success" />
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Upcoming</p>
                      <h3 className="fs-2 fw-bold">{stats.upcoming}</h3>
                    </div>
                    <Clock className="w-6 h-6 text-warning" />
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6} xs={12} className={styles.colWidth}>
                <div className="p-4 bg-white shadow rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted small">Total Revenue</p>
                      <h3 className="fs-2 fw-bold">
                        {formatCurrency(stats.revenue)}
                      </h3>
                      {revenueFilter.enabled && (
                        <small className="text-success">
                          {stats.revenueDateRange} | {stats.revenueBookingCount}{" "}
                          bookings
                        </small>
                      )}
                    </div>
                    <div className="text-success">
                      <Badge bg="success">Revenue</Badge>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Filters and Search */}
            <Container fluid>
              <Row className="justify-content-between align-items-center mb-3">
                <Col
                  xs={12}
                  md={6}
                  className="d-flex gap-3 flex-wrap mb-3 mb-md-0"
                >
                  <Button
                    variant={
                      activeTab === "all" ? "primary" : "outline-primary"
                    }
                    onClick={() => setActiveTab("all")}
                  >
                    All Bookings
                  </Button>
                  <Button
                    variant={
                      activeTab === "agent" ? "primary" : "outline-primary"
                    }
                    onClick={() => setActiveTab("agent")}
                  >
                    Agents
                  </Button>

                  <Button
                    variant={
                      activeTab === "walk_in" ? "primary" : "outline-primary"
                    }
                    onClick={() => setActiveTab("walk_in")}
                  >
                    Walk-in
                  </Button>
                </Col>
                <Col
                  xs={12}
                  md={6}
                  className="d-flex justify-content-md-end justify-content-start"
                >
                  <div className="d-none d-flex gap-2">
                    <InputGroup className={styles.searchWidth}>
                      <FormControl
                        type="search"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Button variant="outline-primary">
                        <Search className="w-5 h-5" />
                      </Button>
                    </InputGroup>
                    <Button
                      variant="outline-secondary"
                      onClick={refreshData}
                      title="Refresh data"
                    >
                      ↻
                    </Button>
                  </div>
                </Col>
              </Row>
            </Container>

            {/* Bookings Table */}
            <div className="deal-table-container">
              <div className="table-responsive" style={{ minHeight: "400px" }}>
                <table className="deal-table">
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>Guest Details</th>
                      <th>Check In/Out</th>
                      <th>Room Type</th>
                      <th>Room No.</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  {isLoading ? (
                    <tbody>
                      <tr>
                        <td colSpan={100} style={{ height: 200 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                            }}
                          >
                            <Spin size="large" />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {currentRows.length === 0 ? (
                        <tr>
                          <td colSpan={100} style={{ height: 200 }}>
                            No bookings.
                          </td>
                        </tr>
                      ) : (
                        currentRows?.map((booking) => (
                          <tr key={booking.booking_id || booking.id}>
                            <td>{booking.id}</td>
                            <td>{getGuestName(booking.guest)}</td>
                            <td>
                              {booking.check_in} - {booking.check_out}
                            </td>

                            <td>
                              {booking.room && Array.isArray(booking.room)
                                ? booking.room
                                    .map((r) => r.room_type_name)
                                    .join(", ")
                                : booking.room
                                  ? booking.room.room_type_name
                                  : ""}
                            </td>
                            <td>
                              {booking.room && Array.isArray(booking.room)
                                ? booking.room
                                    .map((r) => r.room_number)
                                    .join(", ")
                                : booking.room
                                  ? booking.room.room_number
                                  : ""}
                            </td>
                            <td>
                              {booking.source === "walk_in"
                                ? "Walk-in"
                                : booking.source.charAt(0).toUpperCase() +
                                  booking.source.slice(1)}
                            </td>

                            <td>
                              <span
                                className={`badge ${getStatusColor(
                                  booking.status,
                                )}`}
                              >
                                {formatStatus(booking.status)}
                              </span>
                            </td>

                            <td>{formatCurrency(getBookingAmount(booking))}</td>

                            <td>
                              <div className="options-menu">
                                <button className="options-button">⋮</button>
                                <div className="dropdown-menu">
                                  <EditBookingModal
                                    editingItem={booking}
                                    setBookings={setBookings}
                                    bookings={bookings}
                                    hotelId={hotelId}
                                    onSuccess={refreshData}
                                  />
                                  <button
                                    className="delete-btn"
                                    onClick={() =>
                                      confirmDelete(
                                        booking.id,
                                        booking.room &&
                                          Array.isArray(booking.room)
                                          ? booking.room[0]?.id
                                          : booking.room?.id,
                                      )
                                    }
                                  >
                                    Delete
                                  </button>

                                  {filterInvoiceFile(
                                    allInvoices,
                                    booking.id,
                                  ) ? (
                                    <a
                                      href={filterInvoiceFile(
                                        allInvoices,
                                        booking.id,
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <button>Show Invoice</button>
                                    </a>
                                  ) : (
                                    <InvoiceModal
                                      currentBooking={booking}
                                      hotelId={hotelId}
                                      onInvoiceUpdate={refreshData}
                                    />
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  )}
                </table>
              </div>

              <div className="pagination mt-3">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-outline-primary"
                >
                  Previous
                </button>
                <span className="mx-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn btn-outline-primary"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={deleteConfirm.isOpen}
        onConfirm={deleteBooking}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
        message="Are you sure you want to delete this booking?"
      />

      <ToastContainer autoClose={3000} position="top-right" />
    </div>
  );
};

export default Booking;
